/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useEffect, useMemo, useContext } from "react";
import { Box, Typography } from "@mui/material";
import { useDropzone, ErrorCode } from "react-dropzone";
import { ImageUploadItem } from "cms/images/image-selector/dialog-select/upload/image-upload-item";
import { ImageEditContext } from "cms/images/image-selector/context/image-edit-context";
import { useOrgSettings } from "hooks/use-org-settings";
import { autoGenerateFileName, validateFileName } from "cms/files";
import { useAuth } from "cms/auth/hooks/use-auth";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { allowedImageExtensions } from "cms/files";

const focusedStyle = { borderColor: "#2196f3" };
const acceptStyle = { borderColor: "#00e676" };
const rejectStyle = { borderColor: "#ff1744" };
const baseStyle = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
    height: "100%",
    minHeight: "600px",
    borderWidth: 2,
    borderRadius: 4,
    borderColor: "#eeeeee",
    borderStyle: "dashed",
    backgroundColor: "#fafafa",
    color: "#bdbdbd",
    outline: "none",
    transition: "border .24s ease-in-out",
};

// https://stuarttottle.medium.com/upload-to-azure-blob-storage-with-react-34f37805fdfc
// TODO: add caption, image description, image name/title

const UploadImage = () => {

    const { token } = useAuth();
    const imageEditContext = useContext(ImageEditContext);
    const filesReady = imageEditContext?.filesReady;
    const setFilesReady = imageEditContext?.setFilesReady;
    const fileError = imageEditContext?.fileError;
    const setFileError = imageEditContext?.setFileError;
    const statusList = imageEditContext?.statusList;
    const uploadStatus = imageEditContext?.uploadStatus;
    const files = imageEditContext?.files;
    const refs = imageEditContext?.refs;
    const setFiles = imageEditContext?.setFiles;
    const onLoad = imageEditContext?.onLoad;

    const { getOrgSettingsNumber } = useOrgSettings();
    const uploadLimit = getOrgSettingsNumber("upload_limit"); // value in bytes
    const uploadLimitMB = Math.floor(uploadLimit / 1000000);
    const fileTooLargeErrorMessage = `File exceeds maximum size (${uploadLimitMB}MB). Please try again.`;
    const fileInvalidTypeErrorMessage = `File type is not supported. Please try again.\nAccepted file types: ${allowedImageExtensions.join(", ")}`;
    const genericErrorMessage = "There was an issue uploading the file. Please try again.";

    // Dropzone - dropped images are set to 'files', via setFiles
    const {
        getRootProps,
        getInputProps,
        isFocused,
        isDragAccept,
        isDragReject
    } = useDropzone({
        accept: { "image/*": [] },
        maxSize: uploadLimit,
        // Called when the files are dropped. Sets the file data to state
        onDrop: (acceptedFiles, rejectedFiles) => {
            setFileError("");
            rejectedFiles.forEach((file) => {
                file.errors.forEach((error) => {
                    if (error.code === ErrorCode.FileTooLarge) {
                        setFileError(fileTooLargeErrorMessage);
                    } else if (error.code === ErrorCode.FileInvalidType) {
                        setFileError(fileInvalidTypeErrorMessage);
                    } else {
                        setFileError(genericErrorMessage);
                    }
                });
            });
            if (Array.isArray(acceptedFiles) && acceptedFiles.length > 0) {
                // Limit to the first file only
                acceptedFiles = [acceptedFiles[0]];

                // Map over the files to create an array of promises
                const filePromises = acceptedFiles.map(async (file) => {
                    let generatedFileName;
                    const isValidFileName = validateFileName(file?.name, "image");
                    const fileExistResp = await SimpliCityAdminSDK.image.fileExist(token, file?.name);

                    if (isValidFileName && !fileExistResp?.exist) {
                        generatedFileName = file?.name;
                    } else {
                        generatedFileName = autoGenerateFileName(file?.name);
                        const fileExistResp = await SimpliCityAdminSDK.image.fileExist(token, generatedFileName);

                        if (fileExistResp?.exist) {
                            generatedFileName = autoGenerateFileName(file?.name, true);
                        }
                    }

                    const newFile = new File([file], generatedFileName, {
                        type: file.type,
                        lastModified: file.lastModified,
                    });

                    return Object.assign(newFile, {
                        preview: URL.createObjectURL(newFile),
                    });
                });

                // Wait for all promises to resolve
                Promise.all(filePromises).then((resolvedFiles) => {
                    setFiles(resolvedFiles);
                    setFilesReady(true);
                });
            }

        },
    });

    // draws thumbnails to the page
    useEffect(() => {
        // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
        return () => files.forEach((file) => URL.revokeObjectURL(file.preview));
    }, [files]);

    // prepare styles
    const dzstyle = useMemo(
        () => ({
            ...baseStyle,
            ...(isFocused ? focusedStyle : {}),
            ...(isDragAccept ? acceptStyle : {}),
            ...(isDragReject ? rejectStyle : {}),
        }),
        [isFocused, isDragAccept, isDragReject],
    );

    return (
        <>
            <div style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                height: "100%",
                marginTop: "16px",
                marginBottom: "24px",
                alignItems: "stretch",
            }}
            >

                <div style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "16px",
                    alignSelf: "stretch",
                    // height: 'calc(100% - 120px)',
                    height: "100%",
                }}
                >
                    {filesReady &&
                        <div style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "16px",
                            width: "100%",
                            height: "100%",
                        }}
                        >
                            <div className="docs-list">
                                {files.map((file, index) => {
                                    return (
                                        <ImageUploadItem
                                            file={file}
                                            refs={refs}
                                            index={index}
                                            onLoad={onLoad}
                                            key={index}
                                            uploading={statusList[index] ? statusList[index].uploading : false}
                                            progress={statusList[index] ? statusList[index].progress : 0}
                                        />
                                    );
                                }
                                )}
                            </div>
                        </div>
                    }
                    {/* -------  DROPZONE  ------- */}
                    {!filesReady &&
                        <div className="dropzone" {...getRootProps({ dzstyle })} >

                            <input {...getInputProps()} />

                            <div style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "16px",
                                width: "100%",
                                height: "100%",
                            }}
                            >

                                {/*<div className='document-upload-spacer'></div>*/}
                                <div className='image-upload-instruction'>
                                    <p>
                                        Drag &apos;n&apos; drop, or 
                                        <span className="simplicity-active-text">
                                            select some images
                                        </span>
                                    </p>
                                    {fileError &&
                                        <p className="file-error">
                                            <strong>Error: </strong>{fileError}
                                        </p>}
                                </div>

                            </div>
                        </div>
                    }
                </div>

                {/* -------  Action Bar  ------- */}
                {filesReady && files.length > 0 && (
                    <div style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "center",
                        // justifyContent: 'space-between',
                        alignItems: "center",
                        gap: "16px",
                        border: "1px solid #DFDFDF",

                    }}
                    >
                        {/* -------  upload status and button  ------- */}
                        <>
                            <Box style={{
                                padding: "16px 0",
                                // marginLeft: '16px', marginRight: 'auto'
                            }}
                            >
                                <Typography variant="body1">
                                    {/*{uploadStatus.status}:*/}
                                    {uploadStatus.message}
                                </Typography>
                            </Box>

                            {/*<Box style={{ padding: '16px 16px 16px 0' }}>*/}
                            {/*    <Button variant='outlined' onClick={clearFiles}>*/}
                            {/*        Clear*/}
                            {/*    </Button>*/}
                            {/*</Box>*/}
                            {/*<Box style={{ padding: '16px 16px 16px 0' }}>*/}
                            {/*    <Button variant='contained' onClick={onUploadStart}>*/}
                            {/*        Upload Files*/}
                            {/*    </Button>*/}
                            {/*</Box>*/}
                        </>
                    </div>
                )}
            </div>
        </>
    );
};

export default UploadImage;
